import React, { useEffect, useState } from "react";
import { adminGetDailyStatistics, resetDailyTarget } from "../../services/botService";
import ZAIIcon from "../../AI-COMPONENT/ZAIIcon";
import { RotateCcw, Loader2 } from "lucide-react";
import { activateUsersAi, deactivateUsersAi, getAIUserStatus } from "../../services/AIServices";
import AIAccessIcon from "../common/AIAccessIcon";

const AdminDailyStats = () => {
    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [resetting, setResetting] = useState({});
    const [aiStatus, setAiStatus] = useState({}); // { [userId]: { hasAccess: boolean } }
    const [aiLoading, setAiLoading] = useState({}); // per-user status loading
    const [aiToggling, setAiToggling] = useState({}); // per-user toggle loading

    useEffect(() => {
        fetchData();
    }, []);

    const loadAIStatuses = async (users) => {
        // Load AI access status for each user
        const entries = await Promise.all(
            users.map(async (u) => {
                try {
                    setAiLoading((prev) => ({ ...prev, [u.user_id]: true }));
                    const res = await getAIUserStatus(u.user_id);
                    const hasAccess = !!res?.data?.hasAccess;
                    return [u.user_id, { hasAccess }];
                } catch (e) {
                    console.error("Failed to load AI status for user", u.user_id, e);
                    return [u.user_id, { hasAccess: false }];
                } finally {
                    setAiLoading((prev) => ({ ...prev, [u.user_id]: false }));
                }
            })
        );
        setAiStatus((prev) => ({ ...prev, ...Object.fromEntries(entries) }));
    };

    const fetchData = async () => {
        try {
            setLoading(true);
            const response = await adminGetDailyStatistics();
            if (response.success) {
                setData(response.data);
                setFilteredData(response.data);
                await loadAIStatuses(response.data);
            } else {
                setError(response.message || "Failed to fetch data");
            }
        } catch (err) {
            console.error(err);
            setError("Error fetching data");
        } finally {
            setLoading(false);
        }
    };

    const resetTarget = async (userId) => {
        try {
            setResetting((prev) => ({ ...prev, [userId]: true }));
            await resetDailyTarget(userId);

            // refresh stats after reset
            await fetchData();
        } catch (err) {
            console.error(err);
            setError("Error resetting target");
        } finally {
            setResetting((prev) => ({ ...prev, [userId]: false }));
        }
    };

    const toggleAI = async (userId) => {
        try {
            setAiToggling((prev) => ({ ...prev, [userId]: true }));
            const current = aiStatus[userId]?.hasAccess;
            if (current) {
                await deactivateUsersAi(userId);
            } else {
                await activateUsersAi(userId);
            }
            // Refresh single user's status
            setAiLoading((prev) => ({ ...prev, [userId]: true }));
            const res = await getAIUserStatus(userId);
            const hasAccess = !!res?.data?.hasAccess;
            setAiStatus((prev) => ({ ...prev, [userId]: { hasAccess } }));
        } catch (err) {
            console.error("Error toggling AI for user", userId, err);
            setError("Error updating AI access");
        } finally {
            setAiLoading((prev) => ({ ...prev, [userId]: false }));
            setAiToggling((prev) => ({ ...prev, [userId]: false }));
        }
    };

    const handleSearch = (e) => {
        const query = e.target.value.toLowerCase();
        setSearchQuery(query);

        const filtered = data.filter(
            (user) =>
                user.name.toLowerCase().includes(query) ||
                user.email.toLowerCase().includes(query)
        );
        setFilteredData(filtered);
    };

    if (loading) return <p className="text-center py-10"><ZAIIcon /></p>;
    if (error) return <p className="text-center py-10 text-red-500">{error}</p>;

    return (
        <div className="p-4">
            {/* Search Bar */}
            <div className="mb-4">
                <input
                    type="text"
                    value={searchQuery}
                    onChange={handleSearch}
                    placeholder="Search by name or email..."
                    className="w-full p-2 rounded-md border border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white"
                />
            </div>

            {filteredData.map((user) => {
                const totalProfit = user.days.reduce((acc, d) => acc + d.profit, 0);

                const hasAccess = aiStatus[user.user_id]?.hasAccess;
                const statusIsLoading = aiLoading[user.user_id];
                const isToggling = aiToggling[user.user_id];

                return (
                    <div key={user.user_id} className="mb-8 bg-gray-800 rounded-xl p-4 shadow-lg">
                        <div className="flex justify-between items-center">
                            <div>
                                <h2 className="text-xl font-bold text-white">{user.name}</h2>
                                <p className="text-gray-400">{user.email}</p>
                            </div>

                            {/* Actions: AI Access toggle (icon-only) + Reset */}
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => toggleAI(user.user_id)}
                                    disabled={statusIsLoading || isToggling}
                                    title={hasAccess ? "Disable AI access" : "Enable AI access"}
                                    className={`p-2 rounded-lg flex items-center justify-center ${
                                        statusIsLoading || isToggling
                                            ? "bg-gray-500 cursor-not-allowed"
                                            : hasAccess
                                                ? "bg-green-600 hover:bg-green-700"
                                                : "bg-yellow-600 hover:bg-yellow-700"
                                    }`}
                                    aria-label={hasAccess ? "AI enabled - click to disable" : "AI disabled - click to enable"}
                                >
                                    <AIAccessIcon enabled={!!hasAccess} loading={!!(statusIsLoading || isToggling)} size={22} />
                                </button>

                                {/* Reset button with loader */}
                                <button
                                    onClick={() => resetTarget(user.user_id)}
                                    disabled={resetting[user.user_id]}
                                    className={`p-2 rounded-lg flex items-center justify-center ${
                                        resetting[user.user_id]
                                            ? "bg-gray-500 cursor-not-allowed"
                                            : "bg-red-500 hover:bg-red-600"
                                    } text-white`}
                                    title="Reset user's daily target"
                                    aria-label="Reset user's daily target"
                                >
                                    {resetting[user.user_id] ? (
                                        <Loader2 className="h-5 w-5 animate-spin" />
                                    ) : (
                                        <RotateCcw className="h-5 w-5" />
                                    )}
                                </button>
                            </div>
                        </div>

                        <p
                            className={`text-lg font-semibold mt-3 ${
                                totalProfit < 0
                                    ? "text-red-400"
                                    : totalProfit === 0
                                        ? "text-blue-400"
                                        : "text-green-400"
                            }`}
                        >
                            Total Profit: ${totalProfit.toFixed(2)}
                        </p>

                        <div className="space-y-3 mt-3">
                            {user.days.map((dayData, index) => {
                                const rawProgress = (dayData.profit / dayData.target) * 100;
                                const progress = Math.min(Math.max(Math.abs(rawProgress), 0), 100);

                                let barColor = "bg-blue-500";
                                if (dayData.profit < 0) barColor = "bg-red-500";
                                else if (dayData.profit > 0) barColor = "bg-green-500";

                                return (
                                    <div key={index} className="bg-gray-700 p-3 rounded-lg">
                                        <div className="flex justify-between text-gray-200 text-sm mb-1">
                                            <span>{new Date(dayData.day).toLocaleDateString()}</span>
                                            <span>
                                                Profit: ${dayData.profit.toFixed(2)} / Target: ${dayData.target} (
                                                {rawProgress.toFixed(1)}%)
                                            </span>
                                        </div>
                                        <div className="w-full bg-gray-600 h-3 rounded-full">
                                            <div
                                                className={`h-3 rounded-full ${barColor}`}
                                                style={{ width: `${progress}%` }}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

export default AdminDailyStats;
